import { ReactElement, ReactNode } from 'react'
import { GoogleTagManager } from '@next/third-parties/google'
import { NextPage } from 'next'
import { type AppContext, type AppProps, default as NextApp } from 'next/app'
import dynamic from 'next/dynamic'

import Loader from '@/components/base/loader'
import SVGSprites from '@/components/base/svg-sprites'
import Header from '@/components/layout/header'
import { StoreProps, StoreProvider } from '@/contexts/store'
import { ThemeProvider } from '@/contexts/theme'
import { ToastProvider } from '@/contexts/toast'
import TransitionProvider from '@/contexts/transition/transition-provider'
import { getPromiseFromFetch } from '@/utils/request'
import { API, GTM_ID, MOCK_DATA } from '@/constants'

import '@/styles/globals.css'

import '@/base/gsap'

const GridHelper = dynamic(() => import('@/components/base/grid-helper'), {
  ssr: false
})

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  // eslint-disable-next-line no-unused-vars
  getLayout?: (page: ReactElement, props: AppProps) => ReactNode
}

export type AppPropsWithLayout = AppProps &
  StoreProps & {
    Component: NextPageWithLayout
  }

export default function App({ Component, pageProps, menu, strings }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page)

  return (
    <>
      {GTM_ID ? <GoogleTagManager gtmId={GTM_ID} /> : null}
      <StoreProvider menu={menu} strings={strings}>
        <ThemeProvider fluid>
          <ToastProvider>
            {process.env.NODE_ENV === 'development' ? <GridHelper /> : null}
            <Header pageProps={pageProps} />
            <TransitionProvider>
              {getLayout(<Component {...pageProps} />, pageProps)}
            </TransitionProvider>
            <Loader />
            <SVGSprites />
          </ToastProvider>
        </ThemeProvider>
      </StoreProvider>
    </>
  )
}

App.getInitialProps = async (appContext: AppContext) => {
  const appProps = await NextApp.getInitialProps(appContext)

  const { data: menuData } = MOCK_DATA
    ? { data: require(`@/data/menu_locations.json`) }
    : await getPromiseFromFetch({
        method: 'get',
        url: `${API.MENU_LOCATIONS}`
      })

  const { data: optionsData } = MOCK_DATA
    ? { data: require(`@/data/options.json`) }
    : await getPromiseFromFetch({
        method: 'get',
        url: `${API.STRINGS}`
      })

  return {
    ...appProps,
    // failed fetch resolves to an error body: keep `labels` defined so pages do not crash
    strings: optionsData?.labels ? optionsData : { ...optionsData, labels: {} },
    menu: menuData && Object.keys(menuData).length > 0 ? menuData : {}
  }
}
